import type {
  Configuration,
  DeleteGdsgObjectsRequest,
  DeleteGdsgObjectsResponse,
  DownloadGdsgObjectsRequest,
  HttpFile,
  ListGdsgObjectsResponse,
} from '@repo/api-gw-sdk';
import { GdsgEnvironmentsApi } from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/gdsg';

export interface GdsgRegionStatsPointResponse {
  bucketStartMs: number;
  membersProcessed: number;
  failures: number;
  dataLoss?: number;
  queueRowsProcessed: number;
  promoteLatencyP95Ms: number;
  replicationLagP95Ms: number;
  putAckToBackupLagP95Ms: number;
  bundleSizeAvgBytes: number;
  sampleError?: string;
  dataLossSampleError?: string;
  warnings?: GdsgOperationalWarningResponse[];
}

interface GdsgOperationalWarningResponse {
  bucketStartMs?: number;
  code: string;
  message: string;
  count: number;
}

export interface GdsgRegionStatsResponse {
  region: string;
  enabled: boolean;
  points?: GdsgRegionStatsPointResponse[];
  totalStorageBytes?: number;
  storageByBucket?: GdsgStorageByBucketEntryResponse[];
  bucketStats?: GdsgBucketStatsResponse[];
  storageObservedAtMs?: number;
  membersProcessedTotal?: number;
  failuresTotal?: number;
  dataLossTotal?: number;
  replicationLagP95MsMax?: number;
  putAckToBackupLagP95MsMax?: number;
  sampleError?: string;
  warnings?: GdsgOperationalWarningResponse[];
  dataLossSampleError?: string;
}

interface GdsgBucketStatsResponse {
  bucketName?: string;
  points?: GdsgRegionStatsPointResponse[];
  totalStorageBytes?: number;
  membersProcessedTotal?: number;
  failuresTotal?: number;
  dataLossTotal?: number;
  putAckToBackupLagP95MsMax?: number;
  sampleError?: string;
  warnings?: GdsgOperationalWarningResponse[];
  dataLossSampleError?: string;
}

interface GdsgStorageByTierEntryResponse {
  storageClass?: string;
  totalStorageBytes?: number;
}

interface GdsgStorageByBucketEntryResponse {
  bucketName?: string;
  totalStorageBytes?: number;
}

interface GdsgStorageBreakdownEntryResponse {
  bucketName?: string;
  region?: string;
  storageClass?: string;
  totalStorageBytes?: number;
}

export interface GetGdsgRegionStatsResponse {
  windowStartMs: number;
  windowEndMs: number;
  bucketIntervalMs: number;
  regions?: GdsgRegionStatsResponse[];
  storageByBucket?: GdsgStorageByBucketEntryResponse[];
  storageByTier?: GdsgStorageByTierEntryResponse[];
  storageBreakdown?: GdsgStorageBreakdownEntryResponse[];
}

interface GdsgActiveBatchJobsResponse {
  consolidation?: number;
  replication?: number;
}

interface GdsgBigtableClusterStatsResponse {
  serveNodes?: number;
  cpuLoad?: number;
  tableCpuLoads?: GdsgBigtableTableCpuResponse[];
  tableStorageBytes?: GdsgBigtableTableStorageResponse[];
  diskBytesUsed?: number;
  observedAtMs?: number;
}

interface GdsgBigtableTableCpuResponse {
  table?: string;
  cpuLoad?: number;
  observedAtMs?: number;
}

interface GdsgBigtableStorageTypeBytesResponse {
  storageType?: string;
  bytesUsed?: number;
  observedAtMs?: number;
}

interface GdsgBigtableTableStorageResponse {
  table?: string;
  bytesUsed?: number;
  storageTypes?: GdsgBigtableStorageTypeBytesResponse[];
  observedAtMs?: number;
}

interface GdsgQueueLagStatsResponse {
  oldestQueueEnqueueTs?: string;
  oldestQueueAgeMs?: number;
  activeShards?: number;
  observedShards?: number;
}

interface GdsgWorkerHeartbeatStatsResponse {
  observedWorkers?: number;
  worstHeartbeatAtMs?: number;
  worstHeartbeatAgeMs?: number;
  drainingWorkers?: number;
  drainRequestedWorkers?: number;
}

interface GdsgWorkerHeartbeatsResponse {
  consolidation?: GdsgWorkerHeartbeatStatsResponse;
  replication?: GdsgWorkerHeartbeatStatsResponse;
}

interface GdsgDevStatsResponse {
  region: string;
  enabled: boolean;
  activeBatchJobs?: GdsgActiveBatchJobsResponse;
  workerHeartbeats?: GdsgWorkerHeartbeatsResponse;
  consolidationQueueLag?: GdsgQueueLagStatsResponse;
  replicationQueueLag?: GdsgQueueLagStatsResponse;
  bigtable?: GdsgBigtableClusterStatsResponse;
}

export interface GetGdsgDevStatsResponse {
  observedAtMs: number;
  regions?: GdsgDevStatsResponse[];
}

export const gdsg = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const gdsgApi = new GdsgEnvironmentsApi(config);

  return {
    useListEnvironments: (enabled = true) =>
      httpClient.execute(
        gdsgApi.listGdsgEnvironments.bind(gdsgApi),
        enabled ? baseUrl : null,
        'listGdsgEnvironments',
        currentProjectId
      ),
    // useGetRegionStats hits the api-gateway region-stats endpoint directly
    // (instead of going through the generated SDK class) so the FE can ship
    // ahead of the next OpenAPI codegen pass. Swap to the generated method
    // once `getGdsgRegionStats` lands in @repo/api-gw-sdk.
    useGetRegionStats: (
      environmentId: string | undefined,
      sinceMs: number,
      untilMs: number,
      bucketIntervalMs?: number
    ) => {
      const query = new URLSearchParams({
        sinceMs: String(sinceMs),
        untilMs: String(untilMs),
      });
      if (bucketIntervalMs) {
        query.set('bucketIntervalMs', String(bucketIntervalMs));
      }
      const url = environmentId
        ? `/projects/${currentProjectId}/gdsg/environments/${environmentId}/region-stats?${query.toString()}`
        : '';
      return httpClient.get(url, {
        refreshInterval: 30_000,
        // Empty `regions` array is the "no data yet" signal the metrics
        // section uses to render its empty state. We don't retry on error
        // so a transient failure shows the empty state once instead of
        // looping through SWR's backoff retries.
        shouldRetryOnError: false,
      }) as {
        body: GetGdsgRegionStatsResponse | undefined;
        isLoading: boolean;
        error: unknown;
      };
    },
    // useGetDevStats fetches operator-grade diagnostics (worker counts,
    // worker-registry detail, Bigtable cluster shape) for the dev-mode
    // panel. The endpoint is normal-auth on the api-gateway; passing
    // enabled=false makes the URL empty so SWR never fires the request.
    // Caller gates `enabled` on the dev-mode predicate (isDev OR
    // isEmployee && !isDemo && pref.isDevMode) — see gdsgMetricsSection.
    useGetDevStats: (environmentId: string | undefined, enabled: boolean) => {
      const url =
        enabled && environmentId
          ? `/projects/${currentProjectId}/gdsg/environments/${environmentId}/dev-stats`
          : '';
      return httpClient.get(url, {
        refreshInterval: 10_000,
        shouldRetryOnError: false,
      }) as {
        body: GetGdsgDevStatsResponse | undefined;
        isLoading: boolean;
        error: unknown;
      };
    },
    // useListGdsgObjects lists one page of the GCS-style prefix/delimiter
    // listing for the Object Browser. The Objects tab accumulates pages
    // itself (prior pageTokens + their objects) and re-requests per page,
    // so this stays a plain per-page fetch rather than useInfiniteQuery.
    // `enabled` nulls the SWR key so the caller can gate on e.g. the tab
    // being active or a bucket being selected.
    //
    // keepPreviousData is disabled here (unlike the httpClient default):
    // the caller already accumulates prior pages itself, so keeping the
    // previous page's `data` around while the next page is in flight would
    // render that page twice (once from the caller's own committed list,
    // once from this hook still echoing it back) under the same React keys.
    //
    // Empty-string params (prefix/bucket/delimiter/pageToken) are normalized
    // to undefined before both the SWR key and the SDK call: the SDK
    // serializes '' as `?prefix=`, which the api-gateway's OpenAPI validator
    // rejects ("empty value is not allowed"). undefined params are omitted
    // from the query string entirely. The SWR key is built from these same
    // normalized values so e.g. prefix: '' and prefix: undefined share a
    // cache entry instead of silently fragmenting it.
    useListGdsgObjects: (
      params: {
        environmentId: string | undefined;
        region: string;
        role: 'primary' | 'drReplica';
        bucket?: string;
        prefix: string;
        delimiter?: '/';
        pageToken?: string;
        pageSize?: number;
      },
      enabled = true
    ) => {
      const bucket = params.bucket || undefined;
      const prefix = params.prefix || undefined;
      const delimiter = params.delimiter || undefined;
      const pageToken = params.pageToken || undefined;
      return httpClient.executeWithConfig(
        gdsgApi.listGdsgObjects.bind(gdsgApi),
        enabled && params.environmentId ? baseUrl : null,
        'listGdsgObjects',
        { keepPreviousData: false },
        currentProjectId,
        params.environmentId ?? '',
        params.region,
        params.role,
        bucket,
        prefix,
        delimiter,
        pageToken,
        params.pageSize
      ) as {
        data: ListGdsgObjectsResponse | undefined;
        isLoading: boolean;
        error: unknown;
        mutate: () => Promise<unknown>;
      };
    },
    // downloadObjects returns raw bytes for a single key or a zip archive
    // for multiple keys (server picks the format — see openapi description).
    // The caller reads it as a Blob (HttpFile) and drives a browser download.
    downloadObjects: (
      environmentId: string,
      region: string,
      body: DownloadGdsgObjectsRequest
    ): Promise<HttpFile> =>
      gdsgApi.downloadGdsgObjects(
        currentProjectId,
        environmentId,
        region,
        body
      ),
    deleteObjects: (
      environmentId: string,
      region: string,
      body: DeleteGdsgObjectsRequest
    ): Promise<DeleteGdsgObjectsResponse> =>
      gdsgApi.deleteGdsgObjects(currentProjectId, environmentId, region, body),
  };
};
